import { SubNavContainer } from '@/components/sub-nav-container';
import { useStrapi } from '@/hooks/useStrapi';
import { Faq } from '@/lib/types';
import { Button, Pagination } from '@opaz/ui-components';
import { createFileRoute } from '@tanstack/react-router';
import { CirclePlus, MinusCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnalytics } from '@/hooks/useAnalytics';

export const Route = createFileRoute('/faq/')({
  component: RouteComponent,
  context: () => {
    return {
      getBreadcrumbData: () => ({
        id: null,
        translationKey: null,
      }),
    };
  },
  head: () => {
    return {
      meta: [
        {
          title: 'FAQ - OPAZ E-Service Portal',
        },
      ],
    };
  },
});

function RouteComponent() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { t, i18n } = useTranslation();
  const analytics = useAnalytics();

  const locale = i18n.language;
  const pageSize = 10;

  const {
    data: faqs,
    meta,
    isLoading: faqsLoading,
    error: faqsError,
  } = useStrapi<Faq>('faqs', locale, `&populate=*`, currentPage, pageSize);

  // Track API errors
  useEffect(() => {
    if (faqsError && analytics) {
      analytics.trackApiError('faqs', 500);
    }
  }, [faqsError, analytics]);

  // Reset open FAQ when page changes
  useEffect(() => {
    setOpenIndex(null);
  }, [currentPage]);

  const handleFaqToggle = (index: number, faq: Faq, isExpanding: boolean) => {
    if (!analytics) return;

    if (isExpanding) {
      const faqTitleEn = faq.localizations?.find((l) => l.locale === 'en')?.question || faq.question || '';
      analytics.trackFaqExpand(faq.documentId, faqTitleEn);
    }

    setOpenIndex(isExpanding ? index : null);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages = meta?.pagination?.pageCount || 1;

  if (faqsError)
    return (
      <div className="flex justify-center mx-auto py-8 text-red-500" aria-live="polite">
        {t('common.errorMessage')}
      </div>
    );

  return (
    <>
      <SubNavContainer showOneStepBreadcrumbs>
        <div className="max-w-container-960">
          <h1 className="text-3xl font-bold mb-5 text-foreground">{t('faq.title')}</h1>
          <p className="text-xl font-normal text-foreground">{t('faq.subtitle')}</p>
        </div>
      </SubNavContainer>

      <div className="max-w-container-960 mx-auto mt-8 px-6 lg:px-0">
        {faqsLoading
          ? [...Array(3)].map((_, idx) => (
              <div key={idx} className="my-12 animate-pulse" aria-hidden="true">
                <div className="flex items-center justify-between">
                  <div className="h-6 w-3/4 bg-muted rounded" />
                  <div className="h-5 w-10 bg-muted rounded-full" />
                </div>
                <div className="mt-4 space-y-2">
                  <div className="h-4 w-full bg-muted rounded" />
                  <div className="h-4 w-5/6 bg-muted rounded" />
                  <div className="h-4 w-2/3 bg-muted rounded" />
                </div>
                <hr className="mt-6 border-border" />
              </div>
            ))
          : faqs?.map((faq, index) => {
              const isOpen = openIndex === index;
              const contentId = `faq-answer-${index}`;
              return (
                <div key={faq.documentId} className="my-12">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold">{faq.question}</h2>
                    <Button
                      asChild
                      variant="ghost"
                      className="text-link underline underline-offset-8 hover:bg-white hover:text-link/80 inline-flex items-center gap-1 border-none"
                    >
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        aria-controls={contentId}
                        onClick={() => handleFaqToggle(index, faq, !isOpen)}
                      >
                        {isOpen ? <MinusCircle className="w-4 h-4" /> : <CirclePlus className="w-4 h-4" />}
                        {isOpen ? t('serviceDetails.feesHide') : t('serviceDetails.feesShow')}
                      </button>
                    </Button>
                  </div>
                  {isOpen && (
                    <div id={contentId} role="region" aria-labelledby={`faq-question-${index}`} className="py-6">
                      <p className="text-foreground whitespace-pre-wrap">{faq.answer}</p>
                    </div>
                  )}
                  <hr className="mt-3 border-border" />
                </div>
              );
            })}

        {!faqsLoading && totalPages > 1 && (
          <div className="mt-8 mb-12 flex justify-center">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              translations={{
                next: t('common.table.next'),
                previous: t('common.table.previous'),
              }}
            />
          </div>
        )}
      </div>
    </>
  );
}
